import type {
  BillingMeters,
  BillingPeriod,
  BillingPlanOption,
  BillingPlanPrice,
  BillingSummary,
  DunningFacts,
} from "./api.js";
import type { OrgRole } from "./orgs.js";

/**
 * What the billing screen says, decided away from React.
 *
 * Same split as `orgs.ts`: the screens render, this file decides. Everything
 * here is a pure function of what `GET /api/billing` returned, which is what
 * lets the dunning copy, the trial countdown and every meter be tested without
 * a browser. The repository's Vitest setup is a node environment with no
 * DOM, so a testable decision has to be a decision that does not need one.
 *
 * Three rules run through all of it.
 *
 * **The meters cite the same numbers the refusals do.** `quota.used`,
 * `storage.usedBytes` and `seats.used` come from the very functions
 * `createSite`, `assertStorageQuota` and the invitation path enforce with, so
 * a person told "you are out of sites" can come here and see the same integer.
 * Nothing in this file recomputes any of them.
 *
 * **A write is a request, not a result.** Every mutation on the billing routes
 * answers 202: Maxio has accepted the instruction and the org's entitlements do
 * not move until the webhook lands and the inbox worker applies it (plan CD4,
 * one writer). So the screen says "asked for", refetches, and never paints the
 * new plan as though it were already in force.
 *
 * **Unconfigured is ordinary.** dev2 holds no Maxio credentials and neither
 * does CI. The meters still mean something, because they come from entitlements
 * rather than from Maxio, so the page shows them and says plainly that billing
 * is not connected here rather than rendering an error.
 */

// --- who sees the screen -----------------------------------------------------

/**
 * Whether the Billing nav item and screen appear for this role.
 *
 * `owner` or `billing`, and this is the one rule in the dashboard that the role
 * ladder cannot express: `billing` is the ladder's floor, so "owner or billing"
 * is a set and not a threshold, and `roleAtLeast(role, "billing")` would show
 * the screen to everybody.
 *
 * **No longer only a courtesy (LGN-97).** `GET /api/billing` and
 * `GET /api/billing/invoices` gate on the same set server-side, so an admin who
 * types the URL now gets a 403 from the API as well as this refusal, and the
 * screen would show them nothing even if this function let them through. The
 * two are kept in step deliberately: the platform's `BILLING_ROLES` is the
 * control, and this is what stops a customer meeting it as a red box.
 */
export function canSeeBilling(role: OrgRole): boolean {
  return role === "owner" || role === "billing";
}

/**
 * Whether the dunning banner offers its link to the billing screen.
 *
 * The banner itself is for everybody, because it explains why creating a site
 * or uploading a release is being refused, and the people most likely to be
 * refused are exactly the roles billing is closed to. The *link* is not: it
 * would send an admin to a screen that refuses them, which reads as a broken
 * dashboard rather than as a permission. Hidden on the billing screen too,
 * where it would point at the page already open.
 */
export function showBillingLink(role: OrgRole, onBillingScreen: boolean): boolean {
  return !onBillingScreen && canSeeBilling(role);
}

/**
 * Whether this role may start a subscription, change the plan or cancel.
 *
 * `owner` alone, matching `requireAtLeast(role, "owner", …)` on the plan and
 * cancel routes and on the purchase branch of the checkout route. Each of these
 * commits the organization to money it does not owe yet, or takes a service
 * away from everyone in it.
 */
export function canManageBilling(role: OrgRole): boolean {
  return role === "owner";
}

/**
 * Whether this role may replace the card on the subscription that exists.
 *
 * The one write a `billing` contact may make, and it is the one the seat exists
 * for: `docs/organisations-and-roles.md` gives them "invoices and the
 * subscription", and a finance department that cannot replace an expiring card
 * is a finance seat that fails at its only job. Matches
 * `requireBillingRole(role, "add or change a payment method")` followed by the
 * `owner` gate on the purchase branch, so a billing contact is offered the form
 * exactly when the platform would accept it: never on an org with no
 * subscription, where the same gesture would be a purchase.
 */
export function canUpdateCard(role: OrgRole, hasSubscription: boolean): boolean {
  return canManageBilling(role) || (role === "billing" && hasSubscription);
}

// --- the dunning banner ------------------------------------------------------

/** How loudly the banner speaks, and which notice class renders it. */
type NoticeTone = "info" | "warn" | "error";

export interface DunningNotice {
  readonly tone: NoticeTone;
  /** One line, the state in the customer's words. */
  readonly heading: string;
  /** What is blocked right now. Never a threat, always the current fact. */
  readonly detail: string;
  /** Whether to offer the link to the billing screen. */
  readonly showBillingLink: boolean;
}

/**
 * The banner for a dunning state, or null when there is nothing to say.
 *
 * **LGN-72 owns the state; this owns the words.** Every fact here is read off
 * the API and none of it is re-derived, which matters most for day seven: the
 * escalation from `warning` to `restricted` is `effectiveDunningState`
 * subtracting a stored timestamp server-side, and a countdown computed here
 * from `dunningAt` would be a second opinion about when creating stops working.
 * `createBlocked` and `daysUntilBlock` are that decision already made.
 *
 * `createBlocked` is the boolean to branch on rather than the state name, so a
 * later state that also blocks creating gets the right copy without this
 * function being edited. The state chooses the wording; the flag chooses what
 * is claimed about what works.
 *
 * A state nobody recognises renders as silence. The value crosses a network
 * boundary from a service that may be a deploy ahead of the bundle, and telling
 * a paid-up customer their account is in trouble because a new state appeared
 * is a worse failure than showing nothing at all.
 */
export function describeDunning(dunning: DunningFacts): DunningNotice | null {
  switch (dunning.state) {
    /**
     * The first payment failure. A banner and nothing else, which is exactly
     * what `dunning.ts` says this state costs the customer.
     *
     * The sentence about day seven comes from `daysUntilBlock`, and when that
     * is null it is left out rather than guessed. Naming a deadline the server
     * did not give us is how a banner ends up counting down to the wrong day.
     */
    case "warning": {
      const deadline =
        dunning.daysUntilBlock === null
          ? "If it keeps failing, creating sites, uploading releases and inviting people pause."
          : dunning.daysUntilBlock <= 0
            ? "Creating sites, uploading releases and inviting people pause today."
            : `Creating sites, uploading releases and inviting people pause in ${plural(dunning.daysUntilBlock, "day")}.`;
      return {
        tone: "warn",
        heading: "A payment did not go through",
        detail: `Everything still works. Update the card to stop the retries. ${deadline} Published sites keep serving throughout.`,
        showBillingLink: true,
      };
    }

    /**
     * Day seven. Growth stops; serving does not, and saying so is the point of
     * the second sentence: an owner who reads "blocked" and assumes their live
     * site is down will escalate to support over something that has not
     * happened.
     */
    case "restricted": {
      return {
        tone: "error",
        heading: "New sites, uploads and invitations are paused while the payment is outstanding",
        detail:
          "Published sites are still online and people can still read them. Everything resumes " +
          "as soon as a payment goes through, on the plan you are already on.",
        showBillingLink: true,
      };
    }

    /**
     * Dunning exhausted and the Wave 8 cascade has run. The reassurance is
     * load-bearing and it is true: `keepsEntitlements` stays true here, the
     * content is kept, and the retention sweep's 90-day reclaim is the clock
     * that eventually runs out rather than this moment.
     */
    case "suspended": {
      return {
        tone: "error",
        heading: "This organisation is suspended for non-payment",
        detail:
          "Sites are offline and nothing new can be created. Nothing has been deleted: settle " +
          "the account and everything comes back on the same plan.",
        showBillingLink: true,
      };
    }

    // `none`, and anything a later ticket adds. Nothing owed, nothing to say.
    default: {
      return null;
    }
  }
}

/** "1 day" / "3 days", so the banner never reads "in 1 days". */
function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

// --- the trial countdown -----------------------------------------------------

export interface TrialCountdown {
  /** Whole days left, floored at zero. Zero means it ends today or has ended. */
  readonly daysLeft: number;
  readonly expired: boolean;
  /** True inside the last three days, which is when the page starts pressing. */
  readonly ending: boolean;
}

/**
 * How much trial is left, or null when there is no trial to count.
 *
 * Days rather than hours, because "4 days left" is what somebody acts on and
 * "97 hours" is not, and floored rather than rounded, because a countdown that
 * rounds up tells somebody they have a day they do not have.
 *
 * `now` is a parameter rather than a call to `Date.now()` so the boundaries are
 * testable. An unparseable date returns null rather than `NaN` days: the value
 * comes off the wire and a broken timestamp must not put "NaN days left" in
 * front of a customer.
 */
export function trialCountdown(trialEndsAt: string | null, now: Date): TrialCountdown | null {
  if (trialEndsAt === null) return null;
  const ends = new Date(trialEndsAt).getTime();
  if (!Number.isFinite(ends)) return null;

  const msLeft = ends - now.getTime();
  const daysLeft = Math.max(0, Math.floor(msLeft / 86_400_000));
  return { daysLeft, expired: msLeft <= 0, ending: msLeft > 0 && daysLeft <= 3 };
}

// --- the meters --------------------------------------------------------------

export interface MeterReading {
  readonly label: string;
  /** What is being used, already formatted. */
  readonly used: string;
  /** The ceiling, formatted, or null when there is nothing to compare against. */
  readonly limit: string | null;
  /** 0 to 100, or null when there is no ceiling or nothing measured. */
  readonly percent: number | null;
  /** At or over the ceiling: the next attempt is the one that gets refused. */
  readonly exceeded: boolean;
  /** Past four fifths. Worth saying before the refusal, not after. */
  readonly near: boolean;
  /** Why there is no number, when there is none. */
  readonly note: string | null;
}

/**
 * What a cell shows when there is no number to show.
 *
 * Exported so the invoice table and the two formatters below render the same
 * thing: three copies of a placeholder glyph is three chances to drift.
 */
export const NO_VALUE = "\u2013";

/** Bytes as a person reads them. Powers of two, because quotas are set that way. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return NO_VALUE;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** A count, grouped, so six figures of viewers are legible. */
function formatCount(value: number): string {
  return value.toLocaleString("en-NZ");
}

/**
 * One meter, from a used/quota pair.
 *
 * Three shapes fall out of the API and all three are real:
 *
 *   - a number and a ceiling, which is the ordinary case;
 *   - a number and no ceiling (`quota <= 0`), which is how an unlimited or
 *     unset entitlement arrives, shown as the number with no bar;
 *   - no number at all (`used === null`), which is the viewer meter today,
 *     shown as "not measured" and never as zero. Zero would be a lie that reads
 *     as good news.
 */
function reading(
  label: string,
  used: number | null,
  quota: number | null,
  format: (value: number) => string,
  unmeasuredNote: string,
): MeterReading {
  if (used === null) {
    return {
      label,
      used: "Not measured",
      limit: quota === null || quota <= 0 ? null : format(quota),
      percent: null,
      exceeded: false,
      near: false,
      note: unmeasuredNote,
    };
  }
  if (quota === null || quota <= 0) {
    return {
      label,
      used: format(used),
      limit: null,
      percent: null,
      exceeded: false,
      near: false,
      note: "No limit on this plan",
    };
  }
  const percent = Math.min(100, Math.round((used / quota) * 100));
  return {
    label,
    used: format(used),
    limit: format(quota),
    percent,
    exceeded: used >= quota,
    near: used / quota >= 0.8 && used < quota,
    note: null,
  };
}

/**
 * The four meters, in the order the limits bite.
 *
 * Sites and storage first because those are the two refusals an owner actually
 * meets; seats next because it is the one an admin meets; viewers last because
 * it is a soft monthly ceiling and, today, an unmeasured one.
 */
export function readMeters(meters: BillingMeters): readonly MeterReading[] {
  return [
    reading("Sites", meters.sites.used, meters.sites.quota, formatCount, ""),
    reading("Storage", meters.storage.usedBytes, meters.storage.quotaBytes, formatBytes, ""),
    reading("Seats", meters.seats.used, meters.seats.quota, formatCount, ""),
    reading(
      "Monthly viewers",
      meters.viewers.used,
      meters.viewers.quota,
      formatCount,
      // Honest rather than reassuring: the entitlement exists and nothing
      // counts against it yet. See the note on the field in the platform route.
      "Counting distinct monthly viewers is not wired up yet, so nothing is being enforced here.",
    ),
  ];
}

// --- plan changes ------------------------------------------------------------

export type PlanDirection = "upgrade" | "downgrade" | "same";

/**
 * Which way a plan change goes, by the catalog's own rank.
 *
 * Named rather than inferred at the call site because the two directions read
 * differently to a customer: an upgrade is charged now with a proration, a
 * downgrade usually credits and takes effect at a boundary, and calling both
 * "change plan" hides the half people want to know.
 */
export function planDirection(
  from: BillingPlanOption | undefined,
  to: BillingPlanOption,
): PlanDirection {
  if (from === undefined || from.code === to.code) return "same";
  return to.rank > from.rank ? "upgrade" : "downgrade";
}

/**
 * The plans worth showing as a choice.
 *
 * The current one is excluded: a button that moves you to the plan you are
 * already on is a button whose only outcome is a refusal. Everything else in
 * the catalog is listed even when `available` is false, because "Business, talk
 * to us" and "not purchasable in this environment" are both things a customer
 * benefits from seeing; the button, not the row, is what gets disabled.
 */
export function selectablePlans(plans: readonly BillingPlanOption[]): readonly BillingPlanOption[] {
  return plans.filter((plan) => !plan.current).toSorted((a, b) => a.rank - b.rank);
}

/**
 * Money, from the integer minor units Maxio computed. Never re-derived here.
 *
 * **Not every currency has two decimal places** (LGN-127). Dividing by 100 is
 * right for USD, NZD and AUD, the three the Maxio site has enabled today, and
 * wrong by a factor of a hundred for JPY, which has none, and by ten for the
 * three-place currencies. The divisor is therefore taken from the currency
 * itself: `Intl` knows each one's minor units, and asking it is the difference
 * between "¥7,900" and "¥79".
 *
 * An unusable currency code returns the dash rather than throwing: `Intl`
 * raises a `RangeError` on one it does not recognise, and a billing screen must
 * not go blank because a catalog row holds a typo.
 */
function formatCents(cents: number, currency = "USD"): string {
  if (!Number.isFinite(cents)) return NO_VALUE;
  try {
    const format = new Intl.NumberFormat("en-NZ", { style: "currency", currency });
    const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
    return format.format(cents / 10 ** digits);
  } catch {
    return NO_VALUE;
  }
}

/** The word for a period, as a customer would say it. */
function periodUnit(period: BillingPeriod): string {
  return period === "annual" ? "year" : "month";
}

/**
 * The price of one plan for one period, or null when there is none.
 *
 * `prices` is absent on a payload from a platform that predates LGN-127, and
 * either half is null when the catalog carries no synced price for that period
 * or when the plan cannot be bought for it. All three are the same answer to
 * the only question a caller has: can this screen state an amount? A caller
 * that gets null must say so; none of them may substitute a zero, a placeholder
 * or the marketing site's number.
 */
export function planPriceFor(
  plan: BillingPlanOption | undefined,
  period: BillingPeriod,
): BillingPlanPrice | null {
  const prices = plan?.prices;
  if (prices === undefined) return null;
  return (period === "annual" ? prices.annual : prices.monthly) ?? null;
}

/**
 * A plan as it reads in the picker: the name, and what it costs.
 *
 * "Team: US$79.00 per month or US$780.00 per year". Both periods, because the
 * choice between them is made on this screen and a person comparing them should
 * not have to flip a control to see the second number.
 *
 * A plan with no synced price says so in words. That is deliberate and it is
 * the whole point of the ticket: the alternative to an honest sentence is a
 * name with no number beside it, which reads as free.
 */
export function describePlanOption(plan: BillingPlanOption): string {
  const monthly = planPriceFor(plan, "monthly");
  const annual = planPriceFor(plan, "annual");
  const parts: string[] = [];
  if (monthly !== null)
    parts.push(`${formatCents(monthly.priceCents, monthly.currency)} per month`);
  if (annual !== null) parts.push(`${formatCents(annual.priceCents, annual.currency)} per year`);
  if (parts.length === 0) return `${plan.name} (price not published)`;
  return `${plan.name}: ${parts.join(" or ")}`;
}

/** What the checkout screen states beside the button, and whether it can. */
export interface CheckoutAmount {
  /**
   * False stops the form taking a card. A payment form that cannot name the
   * amount has nothing for the customer to agree to (audit H4), so the button
   * is disabled rather than the sentence quietly omitted.
   */
  readonly known: boolean;
  readonly text: string;
}

/**
 * The amount about to be committed to, in a sentence (LGN-127).
 *
 * It changes with the period toggle, which is the second half of the finding:
 * flipping Billed to Annually used to change nothing a customer could see, and
 * it changes the product being bought and the sum being charged.
 *
 * It says the recurring price and deliberately not "you will be charged this
 * today". A Team subscription carries a 14-day trial on the Maxio product, so
 * the first invoice is not always today's, and `billing_plans` holds no trial
 * length to promise one from. Stating the rate is true in both cases.
 */
export function describeCheckoutAmount(
  plan: BillingPlanOption | undefined,
  period: BillingPeriod,
): CheckoutAmount {
  if (plan === undefined) {
    return { known: false, text: "Choose a plan to see what it costs." };
  }
  const price = planPriceFor(plan, period);
  if (price === null) {
    return {
      known: false,
      text:
        `We cannot show what ${plan.name} costs billed ${period === "annual" ? "annually" : "monthly"} yet, ` +
        `so this form will not take a card for it. Ask us and we will price it before you commit.`,
    };
  }
  return {
    known: true,
    text: `Subscribing to ${plan.name} at ${formatCents(price.priceCents, price.currency)} per ${periodUnit(period)}.`,
  };
}

/**
 * The proration in a sentence, from Maxio's four numbers.
 *
 * `paymentDueInCents` is the one that matters: it is what will be taken now.
 * A credit is reported as a credit rather than as a negative charge, because
 * "you will be charged -$14.20" is a sentence no customer should have to parse.
 */
export function describeProration(
  preview: { paymentDueInCents: number; creditAppliedInCents: number },
  currency = "USD",
): string {
  if (preview.paymentDueInCents > 0) {
    return `${formatCents(preview.paymentDueInCents, currency)} will be charged now, prorated for the rest of this period.`;
  }
  if (preview.creditAppliedInCents > 0) {
    return `Nothing is charged now. ${formatCents(preview.creditAppliedInCents, currency)} is credited against future invoices.`;
  }
  return "Nothing is charged now.";
}

// --- what a summary adds up to ----------------------------------------------

/**
 * Whether the page can offer a card form at all.
 *
 * Both halves have to be true and they fail independently: the deployment holds
 * Maxio credentials (`configured`), and the site has issued a Maxio.js
 * publishable key (`checkout`). This is the decision the loader is handed; see
 * `checkout.ts` for what it does with it.
 */
export function canCheckout(summary: BillingSummary): boolean {
  return summary.configured && summary.checkout !== null;
}

/** The four hosted fields, each an iframe drawn into a container of ours. */
export const CHECKOUT_FIELDS = [
  { key: "number", label: "Card number", id: "checkout-number" },
  { key: "month", label: "Month", id: "checkout-month" },
  { key: "year", label: "Year", id: "checkout-year" },
  { key: "cvv", label: "Security code", id: "checkout-cvv" },
] as const;

/**
 * What Maxio.js is told when the card form is drawn.
 *
 * A pure function rather than an object literal inside the effect, so a test
 * can pin what goes over the wire: the four hosted fields, and the 3D Secure
 * flag that Strong Customer Authentication on the Windcave gateway requires
 * (on in NZ as well as in PSD2 territory; Chris, 2026-09-03, LGN-102).
 * Windcave is a pre-authentication gateway, so the cardholder must be in the
 * session: with `threeDSecure` set, Maxio.js opens the challenge itself when
 * `token()` is called, and the token only arrives once the challenge has
 * passed. Without the flag the tokenisation is refused at the gateway.
 */
export function tokeniserConfig(checkout: {
  publicKey: string;
  serverHost: string;
  gatewayHandle: string | null;
}): Record<string, unknown> {
  return {
    publicKey: checkout.publicKey,
    serverHost: checkout.serverHost,
    // Named, not defaulted: a Maxio site can hold several gateways and the
    // default on a shared sandbox is not ours (LGN-88, 2026-09-03). Omitted
    // entirely when the platform has none configured, so a single-gateway
    // site behaves as before.
    ...(checkout.gatewayHandle === null ? {} : { gatewayHandle: checkout.gatewayHandle }),
    type: "card",
    threeDSecure: true,
    // Every field is the provider's iframe. Nothing in this bundle can read
    // what is typed into one, which is the entire point of the arrangement.
    fields: Object.fromEntries(
      CHECKOUT_FIELDS.map((field) => [field.key, { selector: `#${field.id}`, placeholder: "" }]),
    ),
  };
}
